import React, { useEffect } from 'react';
import { Item } from '@baemingo/table-events/dist/types';
import styled from '@emotion/styled';
import CourseTypeBox from './CourseType';
import { DragSourceMonitor, DropTargetMonitor, useDrag, useDrop } from 'react-dnd';
import { useCurrency, useI18n } from '~Hooks';
import { DragTypes } from './Types';
import { CombinedItem } from '~Utils/combineArticles';
import { BOOKINGS_DISCOUNT_ID } from '~Providers/BookingsProvider';
import { MEMBERSHIP_DISCOUNT_ID } from '~Components/LinkUser/consts';
import { Box } from '~Styles';

const ItemWrapper = styled.li<{
  selected: boolean;
  preview?: boolean;
  isParkedOrder?: boolean;
}>`
  list-style: none;
  position: relative;
  border-bottom: 1px solid
    ${({ isParkedOrder }) =>
      isParkedOrder ? 'var(--colorBlueGray1000)' : 'var(--colorBlueGray1200)'};
  padding: 10px 16px 10px 12px;
  border-left: 4px solid;
  border-left-color: ${({ selected, isParkedOrder }) =>
    selected && !isParkedOrder ? 'var(--colorUIYellow)' : 'transparent'};
  align-items: center;

  p {
    padding-right: 6px;
    margin: 0;
    word-break: break-word;
  }

  ${({ preview }) =>
    preview &&
    `
    border: none;
    color: var(--colorBlack);
    background: var(--colorWhite);
    `}
`;

const ItemInnerWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const ItemPrice = styled.span`
  padding-left: 4px;
  margin-left: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const InitialPrice = styled.span`
  color: var(--colorBlueGray600);
  text-decoration: line-through;
  font-size: 12px;
`;

const DiscountName = styled.span`
  color: var(--colorUIOrange);
  font-size: 10px;
  text-align: right;
  display: block;
  margin-bottom: 2px;
  max-width: 74px;
`;

const Qty = styled.span`
  width: 18px;
  margin-right: 8px;
  display: inline-block;
  min-width: 18px;
`;

interface Props {
  item: Item | CombinedItem<Item>;
  onClick: () => void;
  setIsDragging: (isDragging: boolean) => void;
  selected: boolean;
  selectedItems: string[];
  preview?: boolean;
  hidePrice?: boolean;
  disallowDrag?: boolean;
}

const ArticleItem: React.FunctionComponent<Props> = ({
  item,
  onClick,
  setIsDragging,
  selected,
  preview,
  selectedItems,
  hidePrice,
  disallowDrag,
}) => {
  const { i18n } = useI18n();
  const currency = useCurrency();

  const [{ isDragging }, drag] = useDrag({
    item: { type: DragTypes.ITEM, selectedItems },
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: !disallowDrag,
  });

  const [_, drop] = useDrop({
    accept: [DragTypes.TABLE, DragTypes.CHEQUE],
    canDrop: (item: any) => {
      return item.id;
    },
    collect: (monitor: DropTargetMonitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  useEffect(() => {
    if (disallowDrag) {
      setIsDragging(false);
      return;
    }
    setIsDragging(isDragging);
  }, [isDragging, disallowDrag]);

  const itemCount = 'count' in item ? item.count : item.fraction;

  return (
    <ItemWrapper
      onClick={onClick}
      selected={selected}
      preview={preview}
      isParkedOrder={window.location.hash.includes('parked-orders')}
      ref={(node) => (selected ? drag(drop(node)) : null)}>
      <ItemInnerWrapper>
        <Qty>{Math.round(itemCount * 100) / 100}</Qty>
        <p>
          {item.title}
          {item.message && (
            <i>
              <br />
              {item.message}
            </i>
          )}
          {item.richVariations
            ?.filter((rv) => rv.price > 0)
            .map((variation) => (
              <i key={variation.id}>
                <br />+ {variation.name}
              </i>
            ))}
        </p>
        {item.courseId && <CourseTypeBox id={item.courseId} />}
        <ItemPrice>
          {item.discount > 0 && !item.discountGroupId && (
            <DiscountName>
              {item.discountTitle ||
                (item.discountId === BOOKINGS_DISCOUNT_ID
                  ? i18n('Discount.Booking')
                  : item.discountId === MEMBERSHIP_DISCOUNT_ID
                  ? i18n('Discount.Membership')
                  : i18n('Discount.Manual'))}
            </DiscountName>
          )}
          <Box display="flex" gap="xs" alignItems="center">
            {item.discount > 0 && <InitialPrice>{currency.format(item.amount)}</InitialPrice>}
            {currency.format(item.amount - item.discount)}
          </Box>
        </ItemPrice>
      </ItemInnerWrapper>
    </ItemWrapper>
  );
};

export default ArticleItem;
